/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Execution History Panel Component
 * Displays execution history for an AI System with ability to view details
 */

import React, { useState, memo, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  MenuItem,
  Pagination,
  InputAdornment,
  Button,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  PlayArrow as RunningIcon,
  Cancel as CancelledIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { executionApi } from '../../services/api';
import type { AISystemExecution, AISystemExecutionListItem, ExecutionStatus } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { formatCredits, getExecutionCreditUsage } from './runsPresentation';

// Helper functions defined outside component to avoid recreation
const getStatusDisplayHelper = (status: ExecutionStatus) => {
  switch (status) {
    case 'SUCCESS':
    case 'COMPLETED':
      return { icon: <SuccessIcon fontSize="small" />, color: 'success' as const, label: 'Success' };
    case 'FAILED':
    case 'ERROR':
      return { icon: <ErrorIcon fontSize="small" />, color: 'error' as const, label: 'Failed' };
    case 'RUNNING':
      return { icon: <RunningIcon fontSize="small" />, color: 'info' as const, label: 'Running' };
    case 'PENDING':
      return { icon: <PendingIcon fontSize="small" />, color: 'warning' as const, label: 'Pending' };
    case 'CANCELLED':
      return { icon: <CancelledIcon fontSize="small" />, color: 'default' as const, label: 'Cancelled' };
    default:
      return { icon: <PendingIcon fontSize="small" />, color: 'default' as const, label: status };
  }
};

const getExecutionTypeLabelHelper = (type: string) => {
  switch (type.toLowerCase()) {
    case 'manual': return 'Manual Test';
    case 'scheduled': return 'Scheduled';
    case 'api': return 'API Call';
    default: return type;
  }
};

const formatDurationHelper = (start: string, end: string | null) => {
  if (!end) return 'In progress...';
  const durationMs = new Date(end).getTime() - new Date(start).getTime();
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const getActionsSummaryHelper = (execution: any): { total: number; successful: number; failed: number } | null => {
  // Use pre-computed actions_summary from backend (much faster than computing from expert_results)
  const summary = execution.actions_summary;
  if (!summary || summary.total === 0) return null;
  return { total: summary.total, successful: summary.successful, failed: summary.failed };
};

// Memoized execution list item component
interface ExecutionListItemProps {
  execution: AISystemExecutionListItem;
  onViewExecution: (execution: AISystemExecutionListItem) => void;
  showDivider: boolean;
}

const ExecutionListItem = memo<ExecutionListItemProps>(({ execution, onViewExecution, showDivider }) => {
  const statusDisplay = getStatusDisplayHelper(execution.status);
  const isRunning = execution.status === 'RUNNING' || execution.status === 'PENDING';
  const creditUsage = getExecutionCreditUsage(execution);
  
  // Filter to only trading position actions for cleaner summary
  const positionActions = ['open_position', 'close_position', 'modify_position'];
  
  // Compute filtered actions summary (only position actions)
  const actionsSummary = useMemo(() => {
    const summary = (execution as any).actions_summary;
    if (!summary?.actions) return null;
    
    // Filter actions to only position-related ones
    const filtered = summary.actions.filter((action: any) => {
      if (action.type === 'action') {
        return positionActions.includes(action.action_type);
      }
      if (action.type === 'tool_call') {
        return positionActions.includes(action.tool_name);
      }
      return false;
    });
    
    if (filtered.length === 0) return null;
    
    const successful = filtered.filter((a: any) => a.status === 'success').length;
    const failed = filtered.filter((a: any) => a.status === 'failed').length;
    
    return { total: filtered.length, successful, failed };
  }, [execution.actions_summary]);
  
  // Extract action messages from filtered actions
  const actionMessages = useMemo(() => {
    const summary = (execution as any).actions_summary;
    if (!summary?.actions) return [];
    
    return summary.actions
      .filter((action: any) => {
        // For regular actions, check action_type
        if (action.type === 'action') {
          return positionActions.includes(action.action_type) && action.message;
        }
        // For tool calls, check tool_name
        if (action.type === 'tool_call') {
          return positionActions.includes(action.tool_name) && action.message;
        }
        return false;
      })
      .map((action: any) => 
        action.message.length > 60 ? action.message.substring(0, 60) + '...' : action.message
      );
  }, [execution.actions_summary]);

  return (
    <React.Fragment>
      {showDivider && <Divider />}
      <ListItem
        disablePadding
        secondaryAction={
          <Tooltip title="View Details">
            <IconButton edge="end" onClick={() => onViewExecution(execution)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItemButton onClick={() => onViewExecution(execution)}>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Chip icon={statusDisplay.icon} label={statusDisplay.label} color={statusDisplay.color} size="small" />
                <Chip label={getExecutionTypeLabelHelper(execution.execution_type)} size="small" variant="outlined" />
                {isRunning && <CircularProgress size={16} />}
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <Stack spacing={0.5} mt={1}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Started: {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Duration: {execution.execution_time_ms != null
                        ? execution.execution_time_ms >= 60000
                          ? `${(execution.execution_time_ms / 60000).toFixed(1)}m`
                          : execution.execution_time_ms >= 1000
                          ? `${(execution.execution_time_ms / 1000).toFixed(2)}s`
                          : `${execution.execution_time_ms.toFixed(0)}ms`
                        : formatDurationHelper(execution.created_at, execution.completed_at)}
                    </Typography>
                  </Grid>
                  {creditUsage !== null && (
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        Credits: {formatCredits(creditUsage, { compact: true })}
                      </Typography>
                    </Grid>
                  )}
                  {/* Evaluator Averages */}
                  {(execution.average_accuracy !== null || execution.average_hallucination !== null) && (
                    <Grid size={{ xs: 12 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">Evaluator:</Typography>
                        {execution.average_accuracy !== null && (
                          <Chip 
                            label={`Accuracy: ${Number(execution.average_accuracy).toFixed(0)}%`} 
                            size="small" 
                            color={Number(execution.average_accuracy) >= 70 ? 'success' : Number(execution.average_accuracy) >= 50 ? 'warning' : 'error'}
                            sx={{ height: 18, fontSize: '0.65rem' }} 
                          />
                        )}
                        {execution.average_hallucination !== null && (
                          <Chip 
                            label={`Hallucination: ${Number(execution.average_hallucination).toFixed(0)}%`} 
                            size="small" 
                            color={Number(execution.average_hallucination) <= 30 ? 'success' : Number(execution.average_hallucination) <= 50 ? 'warning' : 'error'}
                            sx={{ height: 18, fontSize: '0.65rem' }} 
                          />
                        )}
                      </Box>
                    </Grid>
                  )}
                  {actionsSummary && (
                    <Grid size={{ xs: 12 }}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Actions/Tools:</Typography>
                          <Chip label={`${actionsSummary.total}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          {actionsSummary.successful > 0 && (
                            <Chip label={`✓ ${actionsSummary.successful}`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          {actionsSummary.failed > 0 && (
                            <Chip label={`✗ ${actionsSummary.failed}`} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                        {actionMessages.length > 0 && (
                          <Box sx={{ pl: 0.5 }}>
                            {actionMessages.map((msg: string, idx: number) => (
                              <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', fontStyle: 'italic' }}>
                                • {msg}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            }
          />
        </ListItemButton>
      </ListItem>
    </React.Fragment>
  );
});
ExecutionListItem.displayName = 'ExecutionListItem';

interface ExecutionHistoryPanelProps {
  systemId: number;
  onViewExecution: (execution: AISystemExecution | AISystemExecutionListItem) => void;
  /** Enable auto-refresh polling (default: false) */
  enableAutoRefresh?: boolean;
}

const ExecutionHistoryPanel: React.FC<ExecutionHistoryPanelProps> = ({ systemId, onViewExecution, enableAutoRefresh = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Filter and pagination states
  const [status, setStatus] = useState<string>('');
  const [executionType, setExecutionType] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [hasActions, setHasActions] = useState<string>(''); // '', 'yes', 'no'
  const [actionStatus, setActionStatus] = useState<string>(''); // '', 'success', 'failed', 'mixed'
  const [actionType, setActionType] = useState<string>(''); // '', 'open_position', 'close_position', etc.
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 20; // Match backend default PAGE_SIZE

  // Build query params - ALL filters are now backend-side
  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      ai_system: systemId,
      page,
      page_size: pageSize,
    };
    
    if (status) params.status = status;
    if (executionType) params.execution_type = executionType;
    if (search) params.search = search;
    if (dateFrom) params.created_after = new Date(dateFrom).toISOString();
    if (dateTo) params.created_before = new Date(dateTo).toISOString();
    
    // Add action-based filters to backend query
    if (hasActions) params.has_actions = hasActions;
    if (actionStatus) params.action_status = actionStatus;
    if (actionType) params.action_type = actionType;
    
    return params;
  }, [systemId, page, pageSize, status, executionType, search, dateFrom, dateTo, hasActions, actionStatus, actionType]);

  // Fetch executions with ALL filters handled by backend
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['system-executions', systemId, queryParams],
    queryFn: () => executionApi.list(queryParams),
    refetchInterval: enableAutoRefresh ? 10000 : false, // Only auto-refresh if enabled
    staleTime: 5000, // Consider data fresh for 5 seconds
  });

  // All filtering is done by backend - just use the results directly
  const executions = data?.results || [];

  // Calculate total pages from backend count
  const totalPages = data?.count ? Math.ceil(data.count / pageSize) : 0;

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load execution history. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Refresh */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
        <Typography variant="h6">
          Execution History {data?.count ? `(${data.count})` : ''}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {isMobile && (
            <Tooltip title={showFilters ? 'Hide Filters' : 'Show Filters'}>
              <IconButton size="small" onClick={() => setShowFilters(prev => !prev)}>
                <FilterListIcon color={showFilters ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={isMobile ? showFilters : true}>
      <Box p={2} pt={0}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search logs, results..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              SelectProps={{
                native: false,
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="start">Start</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Type"
              value={executionType}
              onChange={(e) => {
                setExecutionType(e.target.value);
                setPage(1);
              }}
              SelectProps={{
                native: false,
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="api">API</MenuItem>
              <MenuItem value="backtest">Backtest</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Has Actions"
              value={hasActions}
              onChange={(e) => {
                setHasActions(e.target.value);
                setPage(1);
              }}
              SelectProps={{
                native: false,
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">With Actions</MenuItem>
              <MenuItem value="no">No Actions</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Action Result"
              value={actionStatus}
              onChange={(e) => {
                setActionStatus(e.target.value);
                setPage(1);
              }}
              SelectProps={{
                native: false,
              }}
            >
              <MenuItem value="">All Results</MenuItem>
              <MenuItem value="success">All Successful</MenuItem>
              <MenuItem value="failed">Has Failures</MenuItem>
              <MenuItem value="mixed">Mixed Results</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Action Type"
              value={actionType}
              onChange={(e) => {
                setActionType(e.target.value);
                setPage(1);
              }}
              SelectProps={{
                native: false,
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="open_position">Open Position</MenuItem>
              <MenuItem value="close_position">Close Position</MenuItem>
              <MenuItem value="send_alert">Send Alert</MenuItem>
              <MenuItem value="test_open_position">Test Open Position</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="datetime-local"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={() => {
                setSearch('');
                setStatus('');
                setExecutionType('');
                setHasActions('');
                setActionStatus('');
                setActionType('');
                setDateFrom('');
                setDateTo('');
                setPage(1);
              }}
              sx={{ height: '40px' }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Box>
      </Collapse>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && (!executions || executions.length === 0) && (
        <Box p={3}>
          <Alert severity="info">
            {search || status || executionType 
              ? 'No executions found matching your filters.'
              : 'No executions yet. Click "Test Run" to execute this AI System.'
            }
          </Alert>
        </Box>
      )}

      {/* Executions List */}
      {!isLoading && executions && executions.length > 0 && (
        <>
          <List sx={{ p: 0 }}>
            {executions.map((execution, index) => (
              <ExecutionListItem
                key={execution.id}
                execution={execution}
                onViewExecution={onViewExecution}
                showDivider={index > 0}
              />
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(_event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ExecutionHistoryPanel;
